import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, NgZone } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { FinanceiroService } from '../../../services/financeiro.service';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-relatorio-geral',
  standalone: false,
  templateUrl: './relatorio-geral.component.html',
  styleUrl: './relatorio-geral.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RelatorioGeralComponent implements OnInit {
  dados: any = null;
  carregando = false;
  erro: string | null = null;
  showFiltros = false;

  filtroDataInicio: string = '';
  filtroDataFim: string = '';
  filtroNumeroOS: string = '';
  filtroNomeConsumidor: string = '';
  filtroCpfOuCnpj: string = '';
  filtroStatusOS: string = '';
  filtroCep: string = '';
  filtroLogradouro: string = '';
  filtroNumero: string = '';
  filtroBairro: string = '';
  filtroCidade: string = '';
  filtroEstado: string = '';
  filtroTipoVistoria: string = '';
  modoTotal: boolean = true;

  constructor(
    private financeiroService: FinanceiroService,
    private spinner: NgxSpinnerService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.buscar();
  }

  setModoTotal(total: boolean): void {
    this.modoTotal = total;
    if (total) {
      this.filtroDataInicio = '';
      this.filtroDataFim = '';
    } else {
      this.filtroDataInicio = this.getDataHojeInput();
      this.filtroDataFim = this.getDataHojeInput();
    }
  }

  aplicarFiltro(): void {
    this.showFiltros = false;
    this.buscar();
  }

  resetarERecarregar(): void {
    this.modoTotal = true;
    this.filtroDataInicio = '';
    this.filtroDataFim = '';
    this.filtroNumeroOS = '';
    this.filtroNomeConsumidor = '';
    this.filtroCpfOuCnpj = '';
    this.filtroStatusOS = '';
    this.filtroCep = '';
    this.filtroLogradouro = '';
    this.filtroNumero = '';
    this.filtroBairro = '';
    this.filtroCidade = '';
    this.filtroEstado = '';
    this.filtroTipoVistoria = '';
    this.buscar();
  }

  buscar(): void {
    this.carregando = true;
    this.erro = null;
    this.spinner.show();
    this.cdr.markForCheck();

    this.financeiroService.getRelatorioGeral({
      DataInicial: this.filtroDataInicio || undefined,
      DataFinal: this.filtroDataFim || undefined,
      NumeroOS: this.filtroNumeroOS || undefined,
      NomeConsumidor: this.filtroNomeConsumidor || undefined,
      CpfOuCnpj: this.filtroCpfOuCnpj || undefined,
      StatusOS: this.filtroStatusOS || undefined,
      Cep: this.filtroCep || undefined,
      Logradouro: this.filtroLogradouro || undefined,
      Numero: this.filtroNumero || undefined,
      Bairro: this.filtroBairro || undefined,
      Cidade: this.filtroCidade || undefined,
      Estado: this.filtroEstado || undefined,
      TipoVistoria: this.filtroTipoVistoria || undefined
    }).subscribe({
      next: (res) => {
        this.ngZone.run(() => {
          this.dados = res;
          this.carregando = false;
          this.spinner.hide();
          this.cdr.markForCheck();
          try { this.cdr.detectChanges(); } catch (e) {}
        });
      },
      error: () => {
        this.ngZone.run(() => {
          this.erro = 'Erro ao carregar relatório geral';
          this.carregando = false;
          this.spinner.hide();
          this.cdr.markForCheck();
          try { this.cdr.detectChanges(); } catch (e) {}
        });
      }
    });
  }

  get cadastramento(): any[] {
    return this.dados?.cadastramento ?? [];
  }

  get recadastramento(): any[] {
    return this.dados?.recadastramento ?? [];
  }

  get totalCadastramento(): any {
    return this.dados?.totalCadastramento ?? { total: 0, porStatus: [] };
  }

  get totalRecadastramento(): any {
    return this.dados?.totalRecadastramento ?? { total: 0, porStatus: [] };
  }

  get totalGeral(): number {
    return this.dados?.totalGeral ?? 0;
  }

  get totaisAnalise(): any {
    return this.dados?.totaisAnalise ?? {
      cadastramento: { validado: 0, invalido: 0, pendente: 0, total: 0 },
      recadastramento: { validado: 0, invalido: 0, pendente: 0, total: 0 }
    };
  }

  get analiseCadastramento(): any {
    return this.totaisAnalise?.cadastramento ?? { validado: 0, invalido: 0, pendente: 0, total: 0 };
  }

  get analiseRecadastramento(): any {
    return this.totaisAnalise?.recadastramento ?? { validado: 0, invalido: 0, pendente: 0, total: 0 };
  }

  getDataHojeInput(): string {
    return new Date().toISOString().split('T')[0];
  }

  formatarData(data: string): string {
    if (!data) return 'N/A';
    const [ano, mes, dia] = data.split('-');
    return `${dia}/${mes}/${ano}`;
  }

  getStatusColor(status: string): string {
    switch (status?.toLowerCase()) {
      case 'concluida':
      case 'concluído':
        return 'badge-concluida';
      case 'negada':
      case 'negado':
        return 'badge-negada';
      case 'retornar':
        return 'badge-retornar';
      default:
        return 'badge-secundario';
    }
  }

  formatarStatus(status: string): string {
    switch (status?.toLowerCase()) {
      case 'concluida':
        return 'Concluida';
      case 'negada':
        return 'Negada';
      case 'retornar':
        return 'Retornar';
      default:
        return status || '-';
    }
  }

  async exportarParaExcel(): Promise<void> {
    try {
      this.spinner.show();

      if (!this.dados) {
        this.spinner.hide();
        alert('Nenhum dado para exportar.');
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Relatório Geral');

      const getImageBase64 = async (url: string): Promise<string | null> => {
        try {
          const response = await fetch(url);
          const blob = await response.blob();
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const base64 = reader.result as string;
              resolve(base64.split(',')[1]);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } catch {
          return null;
        }
      };

      const [logoCedaeBase64, logoVibetexBase64] = await Promise.all([
        getImageBase64('https://cedae.vibetex.com.br/images/cedae2.jpg'),
        getImageBase64('https://cedae.vibetex.com.br/images/logo_vibetex.png')
      ]);

      let imagemCedaeId: number | null = null;
      let imagemVibetexId: number | null = null;

      if (logoCedaeBase64) {
        imagemCedaeId = workbook.addImage({ base64: logoCedaeBase64, extension: 'jpeg' });
      }
      if (logoVibetexBase64) {
        imagemVibetexId = workbook.addImage({ base64: logoVibetexBase64, extension: 'png' });
      }

      // Linha 1: Título
      worksheet.mergeCells('C1:F1');
      const titleCell = worksheet.getCell('C1');
      titleCell.value = 'Relatório Geral de Vistorias';
      titleCell.font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0062CC' } };
      worksheet.getRow(1).height = 40;

      if (imagemVibetexId !== null) {
        worksheet.addImage(imagemVibetexId, { tl: { col: 2.1, row: 0.05 }, ext: { width: 60, height: 48 } });
      }
      if (imagemCedaeId !== null) {
        worksheet.addImage(imagemCedaeId, { tl: { col: 5.99, row: 0.05 }, ext: { width: 60, height: 48 } });
      }

      worksheet.addRow([]);

      // Período
      const periodo = this.modoTotal
        ? 'Período: Completo'
        : `Período: ${this.formatarData(this.filtroDataInicio)} até ${this.formatarData(this.filtroDataFim)}`;
      worksheet.mergeCells('C3:F3');
      const periodoCell = worksheet.getCell('C3');
      periodoCell.value = periodo;
      periodoCell.font = { bold: true, size: 11, color: { argb: 'FF0062CC' } };
      periodoCell.alignment = { vertical: 'middle', horizontal: 'center' };
      periodoCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F4F8' } };
      worksheet.getRow(3).height = 22;

      worksheet.addRow([]);

      // Total Geral
      worksheet.mergeCells('C5:F5');
      const totalGeralCell = worksheet.getCell('C5');
      totalGeralCell.value = `TOTAL GERAL: ${this.totalGeral}`;
      totalGeralCell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
      totalGeralCell.alignment = { vertical: 'middle', horizontal: 'center' };
      totalGeralCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0062CC' } };
      worksheet.getRow(5).height = 25;

      worksheet.addRow([]);

      // ==== SEÇÃO CADASTRAMENTO ====
      worksheet.mergeCells('C7:F7');
      const cadTitleCell = worksheet.getCell('C7');
      cadTitleCell.value = `CADASTRAMENTO — Total: ${this.totalCadastramento.total}`;
      cadTitleCell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
      cadTitleCell.alignment = { vertical: 'middle', horizontal: 'center' };
      cadTitleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF00ACC1' } };
      worksheet.getRow(7).height = 22;

      // Status do cadastramento
      let rowNum = 8;
      if (this.totalCadastramento.porStatus?.length > 0) {
        this.totalCadastramento.porStatus.forEach((s: any) => {
          worksheet.mergeCells(`C${rowNum}:F${rowNum}`);
          const cell = worksheet.getCell(`C${rowNum}`);
          cell.value = `${s.status}: ${s.total}`;
          cell.font = { size: 10, color: { argb: 'FF006064' } };
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0F7FA' } };
          rowNum++;
        });
      }

      rowNum++;

      // Header da tabela cadastramento
      const cadHeader = worksheet.getRow(rowNum);
      cadHeader.values = ['', '', '#', 'CIDADE', 'BAIRRO', 'TOTAL VISTORIAS'];
      cadHeader.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cadHeader.alignment = { vertical: 'middle', horizontal: 'center' } as any;
      cadHeader.height = 22;
      for (let col = 3; col <= 6; col++) {
        cadHeader.getCell(col).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0062CC' } };
      }
      rowNum++;

      this.cadastramento.forEach((item: any, index: number) => {
        const row = worksheet.addRow(['', '', index + 1, item.cidade || '-', item.bairro || '-', item.totalVistorias]);
        row.getCell(3).alignment = { vertical: 'middle', horizontal: 'center' };
        row.getCell(4).alignment = { vertical: 'middle', horizontal: 'left' };
        row.getCell(5).alignment = { vertical: 'middle', horizontal: 'center' };
        row.getCell(6).alignment = { vertical: 'middle', horizontal: 'center' };
        row.getCell(6).font = { bold: true };
        if (index % 2 === 0) {
          for (let col = 3; col <= 6; col++) {
            row.getCell(col).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE3F2FD' } };
          }
        }
        rowNum++;
      });

      rowNum += 2;

      // ==== SEÇÃO RECADASTRAMENTO ====
      const recadRow = rowNum;
      worksheet.mergeCells(`C${recadRow}:F${recadRow}`);
      const recadTitleCell = worksheet.getCell(`C${recadRow}`);
      recadTitleCell.value = `RECADASTRAMENTO — Total: ${this.totalRecadastramento.total}`;
      recadTitleCell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
      recadTitleCell.alignment = { vertical: 'middle', horizontal: 'center' };
      recadTitleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7B1FA2' } };
      worksheet.getRow(recadRow).height = 22;
      rowNum++;

      if (this.totalRecadastramento.porStatus?.length > 0) {
        this.totalRecadastramento.porStatus.forEach((s: any) => {
          worksheet.mergeCells(`C${rowNum}:F${rowNum}`);
          const cell = worksheet.getCell(`C${rowNum}`);
          cell.value = `${s.status}: ${s.total}`;
          cell.font = { size: 10, color: { argb: 'FF4A148C' } };
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3E5F5' } };
          rowNum++;
        });
      }

      rowNum++;

      const recadHeader = worksheet.getRow(rowNum);
      recadHeader.values = ['', '', '#', 'CIDADE', 'BAIRRO', 'TOTAL VISTORIAS'];
      recadHeader.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      recadHeader.alignment = { vertical: 'middle', horizontal: 'center' } as any;
      recadHeader.height = 22;
      for (let col = 3; col <= 6; col++) {
        recadHeader.getCell(col).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7B1FA2' } };
      }
      rowNum++;

      this.recadastramento.forEach((item: any, index: number) => {
        const row = worksheet.addRow(['', '', index + 1, item.cidade || '-', item.bairro || '-', item.totalVistorias]);
        row.getCell(3).alignment = { vertical: 'middle', horizontal: 'center' };
        row.getCell(4).alignment = { vertical: 'middle', horizontal: 'left' };
        row.getCell(5).alignment = { vertical: 'middle', horizontal: 'center' };
        row.getCell(6).alignment = { vertical: 'middle', horizontal: 'center' };
        row.getCell(6).font = { bold: true };
        if (index % 2 === 0) {
          for (let col = 3; col <= 6; col++) {
            row.getCell(col).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3E5F5' } };
          }
        }
      });

      // Larguras das colunas
      worksheet.getColumn(1).width = 3;
      worksheet.getColumn(2).width = 3;
      worksheet.getColumn(3).width = 6;
      worksheet.getColumn(4).width = 30;
      worksheet.getColumn(5).width = 20;
      worksheet.getColumn(6).width = 20;

      worksheet.pageSetup = {
        orientation: 'portrait',
        paperSize: 9,
        fitToPage: true,
        fitToWidth: 1,
        margins: { top: 0.1, bottom: 0.1, left: 0.1, right: 0.1, header: 0, footer: 0 },
        horizontalCentered: true
      };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      const hoje = new Date();
      const dataArquivo = `${String(hoje.getDate()).padStart(2, '0')}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${hoje.getFullYear()}`;
      saveAs(blob, `relatorio-geral_${dataArquivo}.xlsx`);

      this.spinner.hide();
    } catch {
      this.spinner.hide();
    }
  }
}
